import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FaEdit, FaPrint, FaSearch } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SiteLogo from '../../assets/images/SparkLogistics.jpg';
import { BASE_URL } from '../../api';

const SCHEMA_FIELDS = [
  { key: 'manifestno', label: 'Manifest No', type: 'text', readonly: true },
  {
    key: 'status', label: 'Status', type: 'select-enum', options: [
      'Manifest In - Reached at Destination',
      'Runsheet - Out of Delivery',
      'Delivered',
      'Pending',
      'Return to Hub',
      'Wrong Address'
    ]
  },
];

const IngoingManifest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [ingoingManifests, setIngoingManifests] = useState([]);
  const [selectedIngoing, setSelectedIngoing] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [editedConsignors, setEditedConsignors] = useState({});
  const [editedBookingRemarks, setEditedBookingRemarks] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    outgoingManifestId: '',
    status: 'Manifest In - Reached at Destination',
    remarks: '',
    manifestno: ''
  });

  const token = localStorage.getItem('token');
  const API_BASE = `${BASE_URL}/api`;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';

  // ========== FETCH LIST ==========
  const fetchIngoingManifests = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/getctrl/getincomingmenifest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();

      if (json.success && Array.isArray(json.data)) {
        setIngoingManifests(json.data);
      } else {
        toast.error(json.message || 'Failed to load ingoing manifests');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchIngoingManifests();
  }, [token]);

  // ========== OPEN EDIT (NO API) ==========
  const handleEdit = async (ingoing) => {
    setSelectedIngoing(ingoing);

    const out = ingoing.outgoingManifestId || {};

    setFormData({
      outgoingManifestId: out._id || '',
      status: ingoing.status || 'Manifest In - Reached at Destination',
      remarks: ingoing.remarks || '',
      manifestno: ingoing.manifestno || ''
    });

    let bookingList = ingoing.onlineBooking || [];

    // Merge fully populated bookings to fix missing pickrequestid.location for Origin
    try {
      const allRes = await fetch(`${API_BASE}/onlinebookings`, { headers: { Authorization: `Bearer ${token}` } });
      const allJson = await allRes.json();
      const allBooks = allJson.data || (Array.isArray(allJson) ? allJson : []);
      if (Array.isArray(allBooks)) {
        bookingList = bookingList.map(b => {
          const found = allBooks.find(ab => ab._id === b._id);
          if (!found) return b;
          return {
            ...b,
            pickrequestid: (b.pickrequestid && typeof b.pickrequestid === 'object' && b.pickrequestid.clientid)
              ? b.pickrequestid
              : (found.pickrequestid || b.pickrequestid),
            locationid: b.locationid || found.locationid
          };
        });
      }
    } catch (e) {
      console.error('Failed to merge populated bookings', e);
    }

    setBookings(bookingList);

    // Split consignors & bookingremarks from outgoingManifestId
    const consignorList = (out.consignors || '').split(',').map(s => s.trim());
    const remarkList = (out.bookingremarks || '').split('|').map(s => s.trim());

    const cons = {};
    const rem = {};
    bookingList.forEach((b, i) => {
      const cName = consignorList[i] && consignorList[i] !== 'N/A' ? consignorList[i] : (b.consigneeTOaddress || '');
      cons[b._id] = cName;
      rem[b._id] = remarkList[i] || b.remarks || '';
    });

    setEditedConsignors(cons);
    setEditedBookingRemarks(rem);
    setShowEditModal(true);
  };

  // ========== CLOSE MODAL ==========
  const handleCloseForm = () => {
    setShowEditModal(false);
    setSelectedIngoing(null);
    setBookings([]);
    setEditedConsignors({});
    setEditedBookingRemarks({});
    setFormData({
      outgoingManifestId: '',
      status: 'Manifest In - Reached at Destination',
      remarks: '',
      manifestno: ''
    });
  };

  // ========== SUBMIT LOGIC ==========
  const handleSubmit = async () => {
    if (!selectedIngoing?._id) return toast.error('Missing ingoing manifest');

    const onlineIds = bookings.map(b => b._id);
    const consignors = editedConsignors;
    const bookingremarks = editedBookingRemarks;

    const payload = {
      status: formData.status,
      remarks: formData.remarks,
      online: onlineIds,
      consignors,
      bookingremarks,
      operationflag: 'UPDATE'
    };

    try {
      setIsLoading(true);
      const url = `${API_BASE}/outgoingmanifests/updateincoming/${selectedIngoing._id}`;

      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const json = await res.json();

      if (json.success || res.ok) {
        toast.success('Incoming Manifest updated successfully!');
        handleCloseForm();
        fetchIngoingManifests();
      } else {
        toast.error(json.message || 'Save failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Server error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ========== PRINT FUNCTION ==========
  const handlePrint = (manifest) => {
    try {
      if (!manifest || !manifest.outgoingManifestId) {
        toast.error('No manifest data to print');
        return;
      }

      const out = manifest.outgoingManifestId;
      const linked = out.online || [];
      const doc = new jsPDF('p', 'mm', 'a4');
      const w = doc.internal.pageSize.getWidth();
      const h = doc.internal.pageSize.getHeight();
      const margin = 10;

      // 1. HEADER
      doc.addImage(SiteLogo, 'JPEG', margin, 5, 40, 20);

      doc.setFontSize(20).setFont('helvetica', 'bold');
      doc.setTextColor(0, 51, 102);
      doc.text('SPARK LOGISTICS', w - margin - 5, 12, { align: 'right' });

      doc.setFontSize(10).setFont('helvetica', 'bold');
      doc.setTextColor(0, 102, 51); // Dark Green for Incoming
      doc.text('INCOMING MANIFEST REPORT', w - margin - 5, 18, { align: 'right' });

      doc.setFontSize(8).setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Print Date: ${new Date().toLocaleString('en-GB')}`, w - margin - 5, 23, { align: 'right' });

      // Horizontal Line
      doc.setDrawColor(0, 51, 102);
      doc.setLineWidth(0.5);
      doc.line(margin, 28, w - margin, 28);

      // 2. PRIMARY INFO GRID
      const infoY = 32;
      doc.setFillColor(245, 247, 250);
      doc.rect(margin, infoY, w - (margin * 2), 25, 'F');

      doc.setFontSize(8).setFont('helvetica', 'bold').setTextColor(50, 50, 50);

      const col1 = margin + 2;
      const col2 = 80;
      const col3 = 145;
      const rowH = 6;

      // Labels
      doc.text('INCOMING NO:', col1, infoY + 7);
      doc.text('OUTGOING NO:', col1, infoY + 7 + rowH);
      doc.text('DATE:', col1, infoY + 7 + (rowH * 2));

      doc.text('VEHICLE NO:', col2, infoY + 7);
      doc.text('CO-LOADER:', col2, infoY + 7 + rowH);
      doc.text('DEST HUB:', col2, infoY + 7 + (rowH * 2));

      doc.text('ROUTE:', col3, infoY + 7);
      doc.text('MODE:', col3, infoY + 7 + rowH);
      doc.text('TOTAL LOAD:', col3, infoY + 7 + (rowH * 2));

      // Values Mapping
      doc.setFont('helvetica', 'normal').setTextColor(0, 0, 0);
      doc.text(manifest.manifestno || 'N/A', col1 + 24, infoY + 7);
      doc.text(out.manifestno || 'N/A', col1 + 24, infoY + 7 + rowH);
      doc.text(out.ogdate ? new Date(out.ogdate).toLocaleDateString('en-GB') : 'N/A', col1 + 24, infoY + 7 + (rowH * 2));

      doc.text(out.transportid?.vehicleno || out.vehiclenumber || 'N/A', col2 + 22, infoY + 7);
      doc.text(out.coloader || 'N/A', col2 + 22, infoY + 7 + rowH);
      doc.text(out.desthubid?.branchname || out.desthubid?.branchcode || 'N/A', col2 + 22, infoY + 7 + (rowH * 2));

      const firstBooking = linked[0] || {};
      const originName = out.routeid?.originid?.areaname || firstBooking.locationid?.areaname || 'N/A';
      const destName = out.desthubid?.branchname || out.routeid?.destinationid?.areaname || firstBooking.topincode?.locationname || 'N/A';
      const routeStr = `${originName} TO ${destName}`;
      doc.text(routeStr, col3 + 22, infoY + 7);
      doc.text(out.modeid || 'Surface', col3 + 22, infoY + 7 + rowH);

      const totalBoxes = linked.reduce((sum, item) => sum + (item.noofbox || 0), 0);
      const totalWeight = linked.reduce((sum, item) => sum + (item.actweight || 0), 0).toFixed(2);
      doc.text(`${totalBoxes} Boxes / ${totalWeight} Kg`, col3 + 22, infoY + 7 + (rowH * 2));

      // 3. TABLE DATA
      const tableRows = linked.map((item, i) => {
        const consignorNames = out.consignors ? out.consignors.split(',').map(s => s.trim()) : [];
        const customConsignee = consignorNames[i] && consignorNames[i] !== 'N/A' ? consignorNames[i] : null;

        const consignorName = item.pickrequestid?.clientid?.companyName || 
                              item.pickrequestid?.clientid?.name || 
                              item.consignorid?.companyName || 
                              item.consignorid?.name || 
                              item.clientid?.companyName || 
                              item.clientid?.name || 
                              (typeof item.consignorid === 'string' ? item.consignorid : null) ||
                              item.consignor || 
                              'N/A';

        const rawConsignee = item.consigneeTOaddress || 
                             item.consigneeid?.companyName || 
                             item.consigneeid?.name || 
                             (typeof item.consigneeid === 'string' && item.consigneeid !== 'N/A' ? item.consigneeid : null) || 
                             item.consignee;

        let consigneeName = 'N/A';
        if (customConsignee && customConsignee.trim() !== '' && customConsignee !== 'N/A' && customConsignee !== consignorName) {
          consigneeName = customConsignee;
        } else if (rawConsignee && rawConsignee.trim() !== '' && rawConsignee !== 'N/A' && rawConsignee !== consignorName) {
          consigneeName = rawConsignee;
        }

        return [
          i + 1,
          item.docketNumber || 'N/A',
          consigneeName,
          consignorName,
          item.noofbox || 0,
          item.actweight ? item.actweight.toFixed(2) : '0.00',
          item.pickrequestid?.location || item.fromLocation?.split(' (')[0] || 'N/A',
          out.desthubid?.branchname || item.topincode?.locationname || 'N/A',
          item.ewaybill || 'N/A'
        ];
      });

      autoTable(doc, {
        startY: infoY + 30,
        margin: { left: margin, right: margin },
        head: [['S.No', 'Docket No', 'Consignee', 'Consignor', 'Box', 'Weight', 'Origin', 'Dest City', 'E-Way Bill']],
        body: tableRows,
        theme: 'grid',
        headStyles: {
          fillColor: [0, 51, 102],
          textColor: 255,
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center'
        },
        styles: {
          fontSize: 7,
          cellPadding: 2,
          halign: 'center',
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 22, halign: 'left', fontStyle: 'bold' },
          2: { cellWidth: 35, halign: 'left' },
          3: { cellWidth: 35, halign: 'left' },
          4: { cellWidth: 10, halign: 'center' },
          5: { cellWidth: 15, halign: 'right' },
          6: { cellWidth: 24, halign: 'left' },
          7: { cellWidth: 19, halign: 'left' },
          8: { cellWidth: 20, halign: 'center' }
        },
        didDrawPage: (data) => {
          // Footer
          doc.setFontSize(7).setTextColor(150, 150, 150);
          doc.text('Note: This is a computer generated document and does not require a physical signature for verification.', margin, h - 10);
          doc.text(`Page ${data.pageNumber}`, w - margin - 10, h - 10);
        }
      });

      // 4. SIGNATURE SECTION
      const finalY = doc.lastAutoTable.finalY + 20;
      if (finalY + 30 > h) {
        doc.addPage();
        var currentY = 20;
      } else {
        var currentY = finalY;
      }

      doc.setFontSize(9).setFont('helvetica', 'bold').setTextColor(0, 0, 0);

      const sigW = 50;
      const sigSpacing = (w - (margin * 2) - (sigW * 2)) / 1;

      // Dispatcher (Outgoing)
      doc.line(margin, currentY + 15, margin + sigW, currentY + 15);
      doc.text('Dispatching Hub Auth.', margin + sigW / 2, currentY + 20, { align: 'center' });

      // Receiver (Current Hub)
      doc.line(w - margin - sigW, currentY + 15, w - margin, currentY + 15);
      doc.text('Receiving Hub Auth.', w - margin - sigW / 2, currentY + 20, { align: 'center' });

      doc.save(`Incoming_Manifest_${manifest.manifestno}.pdf`);
      toast.success('Professional Incoming Manifest Generated');
    } catch (err) {
      console.error("PDF Mapping Error:", err);
      toast.error('Failed to generate PDF');
    }
  };

  // ========== SEARCH & PAGINATION ==========
  const filtered = useMemo(() => {
    if (!searchTerm) return ingoingManifests;
    const s = searchTerm.toLowerCase();
    return ingoingManifests.filter(m => {
      const out = m.outgoingManifestId || {};
      const firstBooking = (m.onlineBooking || [])[0] || {};
      return (
        (m.manifestno || '').toLowerCase().includes(s) ||
        (m.status || '').toLowerCase().includes(s) ||
        (out.manifestno || '').toLowerCase().includes(s) ||
        (firstBooking.locationid?.areaname || '').toLowerCase().includes(s) ||
        (firstBooking.topincode?.locationname || '').toLowerCase().includes(s) ||
        (out.vehiclenumber || '').toLowerCase().includes(s)
      );
    });
  }, [ingoingManifests, searchTerm]);

  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // ========== FIELD RENDERER ==========
  const renderField = (field) => {
    const value = formData[field.key] || '';
    const isReadonly = field.readonly;

    const handleChange = (e) => {
      setFormData(prev => ({ ...prev, [field.key]: e.target.value }));
    };

    if (field.type === 'select-enum') {
      return (
        <select value={value} onChange={handleChange}
          className="w-full p-2 border rounded text-sm">
          <option value="">Select {field.label}</option>
          {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }

    if (field.type === 'textarea') {
      return <textarea value={value} onChange={handleChange}
        className="w-full p-2 border rounded text-sm" rows="3" />;
    }

    return (
      <input type={field.type || 'text'} value={value} readOnly={isReadonly}
        onChange={handleChange}
        className={`w-full p-2 border rounded text-sm ${isReadonly ? 'bg-gray-100' : ''}`} />
    );
  };

  // ========== RENDER ==========
  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Incoming Manifest Master</h2>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <div className="relative w-full md:w-80">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search manifest, origin, vehicle..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
            </div>
          </div>

          {/* Table UI - Consistent with Outgoing/Pincode */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">S.No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Incoming No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Outgoing No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Route Info</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Vehicle / Mode</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                  <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-500">Loading manifests...</td></tr>
                ) : paginated.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">No manifests found</td></tr>
                ) : (
                  paginated.map((m, i) => {
                    const out = m.outgoingManifestId || {};
                    const firstBooking = (m.onlineBooking || [])[0] || {};

                    // S.No Logic: Search Persistence Fix
                    const sNo = searchTerm
                      ? ingoingManifests.findIndex(orig => orig._id === m._id) + 1
                      : (currentPage - 1) * itemsPerPage + i + 1;

                    return (
                      <tr key={m._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                        <td className="p-3 font-bold text-blue-700">{m.manifestno || 'N/A'}</td>
                        <td className="p-3 text-gray-700">{out.manifestno || 'N/A'}</td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800 uppercase text-[11px]">
                              {out.routeid?.originid?.areaname || firstBooking.locationid?.areaname || 'N/A'} → {out.desthubid?.branchname || out.routeid?.destinationid?.areaname || firstBooking.topincode?.locationname || 'N/A'}
                            </span>
                            <span className="text-[10px] text-gray-500 italic">
                              {out.ogdate ? new Date(out.ogdate).toLocaleDateString('en-GB') : '-'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{out.vehiclenumber || 'N/A'}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded self-start font-bold mt-1 ${out.modeid === 'Air' ? 'bg-sky-100 text-sky-700' : 'bg-green-100 text-green-700'
                              }`}>
                              {out.modeid?.toUpperCase() || 'SURFACE'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${m.status?.includes('Delivered') ? 'bg-green-100 text-green-700' :
                              m.status?.includes('Destination') ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                            }`}>
                            {m.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => handleEdit(m)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition">
                              <FaEdit size={16} />
                            </button>
                            <button onClick={() => handlePrint(m)} className="p-1.5 text-green-600 hover:bg-green-50 rounded transition">
                              <FaPrint size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination UI */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <span className="text-sm text-gray-600 font-medium">
                Showing <span className="text-blue-600">{Math.min(filtered.length, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="text-blue-600">{Math.min(filtered.length, currentPage * itemsPerPage)}</span> of {filtered.length} entries
              </span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                  Previous
                </button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ========== EDIT MODAL ========== */}
        {showEditModal && selectedIngoing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-xl">
              <h2 className="text-xl font-bold mb-6">Edit Ingoing Manifest</h2>

              {/* Outgoing Summary */}
              <div className="bg-gray-50 p-4 rounded mb-6 text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
                <p><strong>Outgoing:</strong> {selectedIngoing.outgoingManifestId?.manifestno || 'N/A'}</p>
                <p><strong>Origin:</strong> {selectedIngoing.outgoingManifestId?.routeid?.originid?.areaname || bookings[0]?.locationid?.areaname || 'N/A'}</p>
                <p><strong>Dest:</strong> {selectedIngoing.outgoingManifestId?.desthubid?.branchname || selectedIngoing.outgoingManifestId?.routeid?.destinationid?.areaname || bookings[0]?.topincode?.locationname || 'N/A'}</p>
                <p><strong>Vehicle:</strong> {selectedIngoing.outgoingManifestId?.vehiclenumber || 'N/A'}</p>
                <p><strong>Co-Loader:</strong> {selectedIngoing.outgoingManifestId?.coloader || 'N/A'}</p>
                <p><strong>Mode:</strong> {selectedIngoing.outgoingManifestId?.modeid || 'N/A'}</p>
                <p><strong>Date:</strong> {formatDate(selectedIngoing.outgoingManifestId?.ogdate)}</p>
                <p><strong>Hub:</strong> {selectedIngoing.outgoingManifestId?.routeid?.hub || 'N/A'}</p>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {SCHEMA_FIELDS.map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium mb-1">{field.label}</label>
                    {renderField(field)}
                  </div>
                ))}
              </div>

              {/* Bookings */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Bookings ({bookings.length})</h3>
                <div className="border rounded max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2">AWB</th>
                        <th className="p-2">Origin</th>
                        <th className="p-2">Dest</th>
                        <th className="p-2">Consignee</th>
                        <th className="p-2">Consignor</th>
                        <th className="p-2">Mode</th>
                        <th className="p-2">Pin</th>
                        <th className="p-2">Box</th>
                        <th className="p-2">Wt</th>
                        <th className="p-2">E-Way</th>
                        <th className="p-2">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => (
                        <tr key={b._id} className="border-b">
                          <td className="p-2 font-medium">{b.docketNumber}</td>
                          <td className="p-2">{b.pickrequestid?.location || b.fromLocation?.split(' (')[0] || 'N/A'}</td>
                          <td className="p-2">{selectedIngoing?.outgoingManifestId?.desthubid?.branchname || b.topincode?.locationname || 'N/A'}</td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={editedConsignors[b._id] || ''}
                              onChange={e => setEditedConsignors(p => ({ ...p, [b._id]: e.target.value }))}
                              className="w-full p-1 border rounded text-xs"
                              placeholder="Consignee"
                            />
                          </td>
                          <td className="p-2">{b.pickrequestid?.clientid?.companyName || b.pickrequestid?.clientid?.name || b.consignorid?.companyName || b.consignorid?.name || 'N/A'}</td>
                          <td className="p-2">{b.mode}</td>
                          <td className="p-2">{b.topincode?.pincode}</td>
                          <td className="p-2 text-center">{b.noofbox}</td>
                          <td className="p-2">{b.actweight?.toFixed(1)}</td>
                          <td className="p-2">{b.ewaybill}</td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={editedBookingRemarks[b._id] || ''}
                              onChange={e => setEditedBookingRemarks(p => ({ ...p, [b._id]: e.target.value }))}
                              className="w-full p-1 border rounded text-xs"
                              placeholder="Remarks"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button onClick={handleCloseForm} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm">Cancel</button>
                <button onClick={handleSubmit} disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm">
                  {isLoading ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IngoingManifest;